import { Plus, BellOff } from "lucide-react";
import type { TouchEventHandler } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation } from "convex/react";

import { AddDeckDialogContent } from "@/components/AddDeckDialogContent";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useAllDecks } from "@/hooks/useAllDecks";
import { cn } from "@/lib/utils";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";

export const AppSidebar = (sidebarProps: React.ComponentProps<typeof Sidebar>) => {
  const { deckId } = useParams();

  const decks = useAllDecks();

  const totalReviewCount = useMemo(
    () => decks?.reduce((acc, deck) => (!deck.isMuted ? acc + deck.toReviewCount : acc), 0) ?? 0,
    [decks]
  );

  return (
    <Sidebar {...sidebarProps}>
      <SidebarContent>
        <SidebarGroup>
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium uppercase text-gray-500">DECKS</span>
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="icon-sm" variant="muted">
                    <Plus />
                    <span className="sr-only">Add Deck</span>
                  </Button>
                </DialogTrigger>
                <AddDeckDialogContent />
              </Dialog>
            </div>
            <div className="text-right">
              <span className="text-sm font-medium uppercase text-gray-500">TO REVIEW</span>
            </div>
          </div>
          <SidebarGroupContent>
            <SidebarMenu>
              <DeckMenuItem
                name="All Decks"
                toReviewCount={totalReviewCount}
                isSelected={!deckId}
              />
              {decks?.map((deck) => (
                <DeckMenuItem
                  key={deck._id}
                  name={deck.name}
                  deckId={deck._id}
                  toReviewCount={deck.toReviewCount}
                  isSelected={deckId === deck._id}
                  isMuted={deck.isMuted}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
};

interface DeckMenuItemProps {
  name: string;
  deckId?: Id<"decks">;
  toReviewCount: number;
  isSelected: boolean;
  isMuted?: boolean;
}

function DeckMenuItem({ name, deckId, toReviewCount, isSelected, isMuted }: DeckMenuItemProps) {
  const itemLinkRef = useRef<HTMLAnchorElement>(null);
  // Safari mobile won't automatically focus the <a> element when it is clicked, so we do this manually.
  const handleClickOrTap = useCallback(() => {
    itemLinkRef.current?.focus();
  }, []);

  // Safari mobile also doesn't automatically focus the selected element when the sidebar is opened.
  useEffect(() => {
    if (isSelected) {
      itemLinkRef.current?.focus();
    }
  }, [isSelected]);

  // Another bug in safari mobile is that it won't re-enable pointer events if they are set dynamically like we do below.
  // https://stackoverflow.com/questions/69450411/setting-pointer-events-dynamically-on-ios-15-safari-is-unreliable-and-unpredicta
  // This is extremely annoying, and pretty much the only way to get things to work.
  // I tried an alternative approach where I stored the hover state in react local state, but this _also_ caused problems in safari mobile because of
  // altering the dom during onmouseover interfered with subsequent click events, which meant the user had to double-tap to select a deck.
  // See: https://stackoverflow.com/a/46051711/4739687
  const navigate = useNavigate();
  const reviewDeckLink = deckId ? `/review/${deckId}` : "/review";
  const reviewOnTouchEnd: TouchEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    void navigate(reviewDeckLink);
  };

  // Handler for "unmute" button.
  const updateMuteStatus = useMutation(api.decks.updateMuteStatus);
  const onUnmuteClick = useCallback(async () => {
    if (deckId) {
      await updateMuteStatus({ deckId, isMuted: false });
    }
  }, [updateMuteStatus, deckId]);

  return (
    <SidebarMenuItem className="px-0 relative group/menu-item" onClick={handleClickOrTap}>
      <div className={isSelected ? "bg-blue-100 rounded-md" : "hover:bg-gray-100"}>
        <Link
          to={deckId ? `/decks/${deckId}` : "/decks"}
          ref={itemLinkRef}
          className="h-8 pl-3 flex flex-1 cursor-pointer items-center justify-between transition-colors duration-200 pr-3"
        >
          <span
            className={cn(
              "truncate pr-2 max-w-100",
              toReviewCount && !isMuted
                ? "font-bold group-hover/menu-item:max-w-[calc(100%-65px)] group-focus-within/menu-item:max-w-[calc(100%-65px)]"
                : ""
            )}
          >
            {name || "Untitled Deck"}
          </span>
          <span
            tabIndex={-1}
            className={cn(
              "text-sm text-gray-500",
              toReviewCount || isMuted
                ? "font-bold group-hover/menu-item:opacity-0 group-focus-within/menu-item:opacity-0 transition-opacity"
                : ""
            )}
          >
            {!isMuted ? toReviewCount : <BellOff size={16} />}
          </span>
        </Link>

        {toReviewCount > 0 && (
          <div
            className={cn(
              // Use visibility instead of pointer-events to work around iOS Safari not re-enabling pointer events reliably.
              // When hidden, the element is non-interactive; when visible, clicks/taps work across browsers.
              "absolute right-0 top-1/2 -translate-y-1/2 opacity-0 invisible transition-[opacity,visibility] duration-150",
              "group-hover/menu-item:opacity-100 group-hover/menu-item:visible", // Show review/unmute button on hover
              "group-focus-within/menu-item:opacity-100 group-focus-within/menu-item:visible" // Show review/unmute button when the group is focused.
            )}
          >
            {isMuted ? (
              <Button variant="muted" size="sm" onClick={onUnmuteClick}>
                Unmute
              </Button>
            ) : (
              <Button variant="secondary" size="sm" asChild>
                <Link to={reviewDeckLink} onTouchEnd={reviewOnTouchEnd}>
                  Review {toReviewCount}
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </SidebarMenuItem>
  );
}
